import { Decimal, Images3D, useIntl } from '@capsicum/common';

import { FanTokenErrorModalButtonBehaviour, FTOErrorCodes, type FTOErrorModalParams } from '../models';

import { DEFAULT_MIN_PURCHASE_AMOUNT, usePurchaseFTOAmountAtom } from './usePurchaseFTOAmountAtom';

/**
 * Hook that returns typed messages related to FTO endpoints error code
 * @params {string} fanTokenShortCode - shortcode of partner currency
 * @params {number} minAmount - min amount the user can buy.
 *
 * @returns {FetchPaymentFanTokenCostEstimateData}
 */
export const useGetErrorMessagesForFTO = (fanTokenShortCode: string, minAmount?: Decimal) => {
	const { $t } = useIntl();
	const { setPurchaseAmount } = usePurchaseFTOAmountAtom();

	const changeAmount = () => {
		setPurchaseAmount(new Decimal(minAmount || DEFAULT_MIN_PURCHASE_AMOUNT));
	};

	// Sold out Error code, should happen GET lobby + GET cost-estimate + POST buy
	const soldOutErrorMessage: FTOErrorModalParams = {
		buttonText: $t('button.gotIt'),
		description: $t('fanTokenOffering.errorDrawer.soldOut.description', {
			fanTokenShortCode: fanTokenShortCode
		}),
		image: Images3D.ftWarning,
		onMainButtonPressBehaviour: FanTokenErrorModalButtonBehaviour.RedirectToMainApp,
		testID: 'ftoErrorMessage-soldOut',
		title: $t('fanTokenOffering.errorDrawer.soldOut.title', { fanTokenShortCode: fanTokenShortCode })
	};

	// Too many request error code, only POST buy endpoint
	const tooManyRequestsErrorMessage: FTOErrorModalParams = {
		buttonText: $t('fanTokenOffering.errorDrawer.tooManyRequest.mainButton'),
		description: $t('fanTokenOffering.errorDrawer.tooManyRequest.description'),
		image: Images3D.warning,
		onMainButtonPressBehaviour: FanTokenErrorModalButtonBehaviour.RedirectToFirstPage,
		onSecondaryButtonPressBehaviour: FanTokenErrorModalButtonBehaviour.RedirectToMainApp,
		secondaryActionButtonText: $t('fanTokenOffering.errorDrawer.tooManyRequest.secondaryButton'),
		testID: 'ftoErrorMessage-tooManyRequest',
		title: $t('fanTokenOffering.errorDrawer.tooManyRequest.title')
	};

	// Amount not available, only POST buy endpoint
	const amountNotAvailable: FTOErrorModalParams = {
		buttonText: $t('fanTokenOffering.errorDrawer.amountNotAvailable.mainButton'),
		description: $t('fanTokenOffering.errorDrawer.amountNotAvailable.description'),
		image: Images3D.ftWarning,
		onMainButtonPress: changeAmount,
		onMainButtonPressBehaviour: FanTokenErrorModalButtonBehaviour.RedirectToFirstPage,
		onSecondaryButtonPressBehaviour: FanTokenErrorModalButtonBehaviour.RedirectToMainApp,
		secondaryActionButtonText: $t('fanTokenOffering.errorDrawer.amountNotAvailable.secondaryButton'),
		testID: 'ftoErrorMessage-amountNotAvailable',
		title: $t('fanTokenOffering.errorDrawer.amountNotAvailable.title')
	};

	// Default error for any other reasons.
	const defaultErrorMessage: FTOErrorModalParams = {
		buttonText: $t('payments.ftoFailedTransaction.button'),
		description: $t('payments.ftoFailedTransaction.description'),
		image: Images3D.warning,
		onMainButtonPressBehaviour: FanTokenErrorModalButtonBehaviour.RedirectToMainApp,
		testID: 'ftoErrorMessage-defaultError',
		title: $t('payments.ftoFailedTransaction.title')
	};

	// AmountNotAvailable and AmountNotAvailable2 needed to fit the FTOErrorCodes type validation
	const errorMessages: Record<FTOErrorCodes, FTOErrorModalParams> = {
		[FTOErrorCodes.AmountNotAvailable]: amountNotAvailable,
		[FTOErrorCodes.DefaultError]: defaultErrorMessage,
		[FTOErrorCodes.NotOpen]: soldOutErrorMessage,
		[FTOErrorCodes.SoldOut]: soldOutErrorMessage,
		[FTOErrorCodes.TooManyRequests]: tooManyRequestsErrorMessage
	};

	return errorMessages;
};
